import React from 'react';
import {
  StyledCardContainer,
  StyledPageContainer,
  StyledFilterBar,
  StyledTabsContainer,
  StyledLayoutGrid,
  StyledLayoutMain,
  StyledLayoutSide,
} from './styles';
import {
  CardContainerProps,
  PageContainerProps,
  FilterBarProps,
  TabsContainerProps,
  LayoutGridProps,
} from './types';

/**
 * CardContainer - Flexible card component with multiple variants
 * @param variant - 'elevated' (default), 'outlined', 'filled', 'compact'
 * @param padding - 'sm', 'md' (default), 'lg'
 * @param hoverable - Enable hover effects (default: false)
 */
export const CardContainer = React.forwardRef<HTMLDivElement, CardContainerProps>(
  (props, ref) => {
    const { children, variant = 'elevated', padding = 'md', hoverable = false, className } = props;
    return (
      <StyledCardContainer
        ref={ref}
        $variant={variant}
        $padding={padding}
        $hoverable={hoverable}
        className={className}
      >
        {children}
      </StyledCardContainer>
    );
  }
);
CardContainer.displayName = 'CardContainer';

/**
 * PageContainer - Main page layout container with max-width and padding
 * @param maxWidth - Custom max-width (default: '1600px')
 */
export const PageContainer = React.forwardRef<HTMLDivElement, PageContainerProps>(
  (props, ref) => {
    const { children, maxWidth, className } = props;
    return (
      <StyledPageContainer ref={ref} $maxWidth={maxWidth} className={className}>
        {children}
      </StyledPageContainer>
    );
  }
);
PageContainer.displayName = 'PageContainer';

/**
 * FilterBar - Container for filter controls with consistent styling
 */
export const FilterBar = React.forwardRef<HTMLDivElement, FilterBarProps>(
  (props, ref) => {
    const { children, className } = props;
    return (
      <StyledFilterBar ref={ref} className={className}>
        {children}
      </StyledFilterBar>
    );
  }
);
FilterBar.displayName = 'FilterBar';

/**
 * TabsContainer - Container for tab navigation with consistent styling
 */
export const TabsContainer = React.forwardRef<HTMLDivElement, TabsContainerProps>(
  (props, ref) => {
    const { children, className } = props;
    return (
      <StyledTabsContainer ref={ref} className={className}>
        {children}
      </StyledTabsContainer>
    );
  }
);
TabsContainer.displayName = 'TabsContainer';

/**
 * LayoutGrid - Two-column layout with main content and sidebar
 * @param mainRatio - Main column width (default: '70%')
 * @param sideRatio - Side column width (default: '28%')
 * @param gap - Gap between columns (default: '2%')
 */
export const LayoutGrid = React.forwardRef<HTMLDivElement, LayoutGridProps>(
  (props, ref) => {
    const { children, mainRatio = '70%', sideRatio = '28%', gap = '2%', className } = props;
    return (
      <StyledLayoutGrid
        ref={ref}
        $mainRatio={mainRatio}
        $sideRatio={sideRatio}
        $gap={gap}
        className={className}
      >
        {children}
      </StyledLayoutGrid>
    );
  }
);
LayoutGrid.displayName = 'LayoutGrid';

/**
 * LayoutMain - Main content area for LayoutGrid (use within LayoutGrid)
 */
export const LayoutMain = StyledLayoutMain;
LayoutMain.displayName = 'LayoutMain';

/**
 * LayoutSide - Sidebar area for LayoutGrid (use within LayoutGrid)
 * @param $top - Sticky position top offset
 */
export const LayoutSide = StyledLayoutSide;
LayoutSide.displayName = 'LayoutSide';

export type { CardContainerProps, PageContainerProps, FilterBarProps, TabsContainerProps, LayoutGridProps } from './types';
export type { CardVariant, ContainerSize } from './types';
